/**
 * Referring-collection context for PDP breadcrumbs.
 *
 * Write (PLP): overwrite sessionStorage with the handle from the current
 * page URL on every collection visit and every product-card interaction.
 * Last collection wins. Search clears storage — the search-results
 * `.product-grid` (non-collection path) and header predictive search
 * product navigation, which never loads that grid. Do not persist the
 * current pathname on a predictive-search click: the shopper is still
 * on the PLP URL, so that would re-write the old handle.
 *
 * Read (PDP): if that handle is in the Liquid allowlist, rewrite the
 * collection crumb from the allowlist entry (never from storage).
 */

const STORAGE_KEY = 'breadcrumb-referring-collection'
const EXCLUDED_HANDLES = new Set(['all', 'frontpage'])

/**
 * @param {string} pathname
 * @returns {string | null}
 */
function collectionHandleFromPath(pathname) {
  if (pathname.includes('/products/')) return null

  const match = pathname.match(/\/collections\/([^/]+)/)
  const captured = match?.[1]
  if (!captured) return null

  const handle = decodeURIComponent(captured)
  if (EXCLUDED_HANDLES.has(handle)) return null
  return handle
}

/**
 * @returns {string | null}
 */
function readStoredHandle() {
  try {
    return sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * @param {string | null} handle
 */
function writeStoredHandle(handle) {
  try {
    if (handle) {
      sessionStorage.setItem(STORAGE_KEY, handle)
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // sessionStorage can throw in private mode
  }
}

function persistCurrentCollection() {
  writeStoredHandle(collectionHandleFromPath(window.location.pathname))
}

function clearStoredHandle() {
  writeStoredHandle(null)
}

/**
 * @param {string | null | undefined} href
 * @returns {boolean}
 */
function isProductHref(href) {
  if (!href) return false

  try {
    return new URL(href, window.location.origin).pathname.includes('/products/')
  } catch {
    return href.includes('/products/')
  }
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isProductCardClick(event) {
  const target = event.target
  if (!(target instanceof Element)) return false

  const grid = target.closest('.product-grid')
  if (!grid) return false

  return Boolean(
    target.closest(
      'product-card, product-card-link, a.product-card__link, a.card-gallery, a[href*="/products/"]'
    )
  )
}

function initWriteSide() {
  if (!document.querySelector('.product-grid')) return

  persistCurrentCollection()
  window.addEventListener('pageshow', persistCurrentCollection)

  /** @param {Event} event */
  const persistIfProductCard = (event) => {
    if (!isProductCardClick(event)) return
    persistCurrentCollection()
  }

  // pointerdown runs before click/navigation; click covers keyboard activation.
  document.addEventListener('pointerdown', persistIfProductCard, true)
  document.addEventListener('click', persistIfProductCard, true)
}

/**
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
function predictiveSearchRoot(event) {
  const target = event.target
  if (!(target instanceof Element)) return null

  const search = target.closest('predictive-search-component')
  if (!(search instanceof HTMLElement)) return null
  if (!search.querySelector('[data-search-results]')) return null

  return search
}

/**
 * Header predictive search never loads the search-results `.product-grid`,
 * so persistCurrentCollection() never runs with a non-collection path.
 *
 * @param {Event} event
 * @returns {boolean}
 */
function isPredictiveSearchProductNavigation(event) {
  const search = predictiveSearchRoot(event)
  if (!search) return false

  if (event instanceof KeyboardEvent) {
    if (event.key !== 'Enter') return false
    // Enter → product is handled on the search input (click() or location.href).
    if (!(event.target instanceof HTMLInputElement)) return false

    const singleResult = search.querySelector('[data-single-result-url]')
    if (
      singleResult instanceof HTMLElement &&
      isProductHref(singleResult.dataset.singleResultUrl)
    ) {
      return true
    }

    const selected = search.querySelector('[aria-selected="true"]')
    if (!(selected instanceof Element)) return false

    return Boolean(
      selected.closest('.predictive-search-results__card--product') ||
        selected.querySelector('a[href*="/products/"]')
    )
  }

  const target = event.target
  if (!(target instanceof Element)) return false

  return Boolean(
    target.closest(
      '.predictive-search-results__card--product, a[href*="/products/"]'
    )
  )
}

function initSearchClear() {
  /** @param {Event} event */
  const clearIfPredictiveSearchProduct = (event) => {
    if (!isPredictiveSearchProductNavigation(event)) return
    clearStoredHandle()
  }

  // pointerdown runs before click/navigation; click covers keyboard
  // activation via .click(); keydown covers Enter → location.href on
  // the single-result shortcut (no click is dispatched).
  document.addEventListener('pointerdown', clearIfPredictiveSearchProduct, true)
  document.addEventListener('click', clearIfPredictiveSearchProduct, true)
  document.addEventListener('keydown', clearIfPredictiveSearchProduct, true)
}

function initReadSide() {
  const root = document.querySelector('[data-breadcrumb-context]')
  if (!(root instanceof HTMLElement)) return

  const storedHandle = readStoredHandle()
  writeStoredHandle(null)

  if (!storedHandle) return

  const jsonEl = root.querySelector('[data-breadcrumb-collections]')
  if (!jsonEl) return

  /** @type {Array<{handle?: string, title?: string, url?: string}>} */
  let allowlist = []
  try {
    allowlist = JSON.parse(jsonEl.textContent || '[]')
  } catch {
    return
  }

  if (!Array.isArray(allowlist)) return

  const match = allowlist.find((entry) => entry?.handle === storedHandle)
  if (!match?.title || !match.url) return

  const collectionCrumb =
    root.querySelector('[data-breadcrumb-collection]') ||
    root.querySelectorAll('.breadcrumbs__link')[1]

  if (!(collectionCrumb instanceof HTMLAnchorElement)) return

  collectionCrumb.textContent = match.title
  collectionCrumb.setAttribute('href', match.url)
}

initWriteSide()
initSearchClear()
initReadSide()
